import React, { useState, useEffect } from 'react';
import firebaseDb from "../firebase-config";
import { FormControl, InputLabel, Select, MenuItem, Stack, Button } from "@mui/material";
import { BookingListData, BookingListViewer, BookingListDownload, ConfirmationData, ConfirmationsViewer, ConfirmationsDownload, InvoiceData, InvoicesViewer, InvoicesDownload, SubjectLineData, SubjectLineViewer, SubjectLineDownload } from '../components/GeneratePDF';
import * as XLSX from "xlsx";
import { formatDayMonthDateYearUTC } from '../dateFormatting';

function Documents() {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.toLocaleString('default', { month: 'long' });

    const [month, setMonth] = useState(currentMonth);
    const [year, setYear] = useState(currentYear);
    const [venues, setVenues] = useState({});
    const [events, setEvents] = useState({});
    // used for year selector
    const [yearsList, setYearsList] = useState();

    useEffect(() => {
        // get list of venues on render
        firebaseDb.child('database/venues').on('value', snapshot => {  
            if (snapshot.val() != null) {  
                setVenues(snapshot.val());
            }
        })

        // get list of events for year selector
        firebaseDb.child('database/events').orderByChild('venue').on('value', (snapshot) => {  
            setEvents((snapshot.val() !== undefined) ? snapshot.val() : {});
        }); 
    }, [])

    useEffect(() => {
        let tempYearsList = [];

        // add each year to tempYearsList
        if (events !== null && Object.keys(events).length !== 0) {
            tempYearsList = Object.keys(events).map((key) => {        
                return events[key].year;
            });
        }
        // filter out duplicates and undefined entries
        let uniqueYears = [];
        tempYearsList = tempYearsList.filter((tempYear) => {
            if (tempYear !== undefined && !uniqueYears.includes(tempYear)) {
                uniqueYears.push(tempYear);
                return true;
            } else {
                return false;
            }
        });
        // sort by year
        tempYearsList.sort();

        setYearsList(tempYearsList);
    }, [events])

    const handleMonthChange = (event) => {
        setMonth(event.target.value);
    };

    const handleYearChange = (event) => {
        setYear(event.target.value);
    };

    const togglePreview = (event) => {
        const venueCardID = event.target.id.split('-preview')[0];
        const venueCard = document.getElementById(venueCardID);

        if (venueCard.style.display === "none") {
            venueCard.style.display = "block";
        } else {
            venueCard.style.display = "none";
        }
    };
    

    function formatDateToCustomFormat(dateString) {
        // Uses the shared dateFormatting.js utility (UTC variant, matching
        // this function's original behavior) instead of a local copy of the
        // day/month abbreviations — see dateFormatting.js for why this
        // matters: a drift between this file's abbreviations and
        // GeneratePDF.js's has silently broken GMass attachments before.
        return formatDayMonthDateYearUTC(new Date(dateString));
    }

    function generateAndDownloadExcel(month, year) {
        // Convert month name to month number (e.g., 'January' to '01')
        const monthNumber = ("0" + (new Date(`${month} 1 ${year}`).getMonth() + 1)).slice(-2);
    
        // Determine the start and end dates for the month
        const startDate = new Date(Date.UTC(year, parseInt(monthNumber) - 1, 1));
        const endDate = new Date(Date.UTC(year, parseInt(monthNumber), 0, 23, 59, 59, 999)); // Up to the last millisecond of the last day of the month
    
        let staticEmail = "musicmattersbookings@gmail.com"; // Static email for all rows
    
    
        const startDateString = `${startDate.getUTCFullYear()}-${("0" + (startDate.getUTCMonth() + 1)).slice(-2)}-${("0" + startDate.getUTCDate()).slice(-2)}`;
        const endDateString = `${endDate.getUTCFullYear()}-${("0" + (endDate.getUTCMonth() + 1)).slice(-2)}-${("0" + endDate.getUTCDate()).slice(-2)}`;
    
        // Initialize the Firebase references
        const eventsRef = firebaseDb.child('database/events');
        const clientsRef = firebaseDb.child('database/clients');
        const venuesRef = firebaseDb.child('database/venues');
    
        // Fetch venues data first
        venuesRef.once('value').then(venuesSnapshot => {
            const venuesData = venuesSnapshot.val();

            // Query events by venue_month_year (one query per venue, then merge).
            //
            // WHY: the previous approach used orderByChild('date').startAt/endAt with
            // a "YYYY-MM-DD" string range. Firebase string queries silently skip any
            // event whose 'date' field is stored as a number (Unix timestamp) rather
            // than a string — those events exist in the database and are found by the
            // PDF-generation query (which uses venue_month_year), but were invisible
            // to this xlsx export.  Switching to the same venue_month_year index that
            // GeneratePDF.js uses guarantees both outputs always see identical events.
            const venueIDs = Object.keys(venuesData);
            Promise.all(
                venueIDs.map(venueID =>
                    eventsRef.orderByChild('venue_month_year').equalTo(`${venueID}__${month}__${year}`).once('value')
                )
            ).then(snapshots => {
                const eventsData = {};
                snapshots.forEach(snapshot => {
                    if (snapshot.val()) Object.assign(eventsData, snapshot.val());
                });

                // If there are no events for the selected month/year, tell the user —
                // previously this returned silently, which looked identical to a real
                // failure (button does nothing either way).
                if (Object.keys(eventsData).length === 0) {
                    alert(`No events found for ${month} ${year} — nothing to generate. Double-check the month/year selected and that events exist for it.`);
                    return;
                }

                // Fetch all clients
                clientsRef.once('value').then(clientsSnapshot => {
                    try {
                        const clientsData = clientsSnapshot.val();

                        let venueDataMap = {};

                        Object.values(eventsData).forEach(event => {
                            // No secondary date filter needed — venue_month_year already
                            // scopes results to exactly this month and year.
                            if (!event.clientID) {  // Safety check: if event does not have a client ID, skip this iteration
                                console.warn(`Event missing client ID. Event data:`, event);
                                return;
                            }
    
                            let client = clientsData[event.clientID];
                            if (!client) {  // Safety check: if client data is not found, skip this iteration
                                console.warn(`No client data found for event with client ID: ${event.clientID}`);
                                return;
                            }
    
                            let email = client.email;
    
                            // Extract the venue name using the venue ID from the event
                            let venue = venuesData[event.venue];
                            let venueName = (venue && venue.name === "Renaissance-Exchange") ? "Exchange" : (venue ? venue.name : "UnknownVenue");
    
                            let timeSuffix = (event.startTime === "17:00" ? " #1" : " #2");
                            let formattedDate = formatDateToCustomFormat(event.date);
                            let confirmationFileName = venueName + "-Artist Confirmation-" + formattedDate + timeSuffix + ".pdf";
    
                            // Group data by venue
                            if (!venueDataMap[venueName]) {
                                venueDataMap[venueName] = [];
                            }
    
                            venueDataMap[venueName].push({
                                "EmailAddress": email, // Artist's actual email is now the true recipient
                                "Cc": staticEmail, // musicmattersbookings@gmail.com copied visibly via Cc
                                "Attachment1": confirmationFileName,
                                "SubjectLine": confirmationFileName.replace(".pdf", "") // Removing ".pdf" from the filename
                            });
                        });
    
                        // Now create separate Excel files for each venue.
                        //
                        // IMPORTANT: these used to fire back-to-back in the same
                        // instant via a plain forEach. Multiple simultaneous
                        // programmatic downloads are a well-known pattern browsers
                        // silently block (Brave in particular is aggressive about
                        // this) — with NO JavaScript error, so our try/catch above
                        // would never see it. Symptom matches exactly what was
                        // reported: code runs fine, real dates get processed, no
                        // alert fires, but nothing (or only the first file) lands
                        // in Downloads. Fix: stagger each write with an increasing
                        // delay so the browser sees them as distinct actions
                        // rather than a burst.
                        const venueNames = Object.keys(venueDataMap);
                        let successCount = 0;
                        let failCount = 0;

                        venueNames.forEach((venueName, index) => {
                            setTimeout(() => {
                                try {
                                    let excelData = venueDataMap[venueName];

                                    // Sort the data based on the filename (date and time).
                                    // NOTE: this re-parses the date back out of the already-
                                    // formatted filename string, which is fragile — any
                                    // unexpected format here throws, and previously that
                                    // failure was invisible to the user. Now it's caught
                                    // below and shown as a real alert instead.
                                    excelData.sort((a, b) => {
                                        const extractDateComponents = (filename) => {
                                            const datePart = filename.split("-Artist Confirmation-")[1];
                                            const month = datePart.split(",")[1].trim().split(" ")[0];
                                            const day = parseInt(datePart.split(",")[1].trim().split(" ")[1], 10);
                                            const year = parseInt(datePart.split(",")[2].trim(), 10);
                                            const timeSuffix = filename.includes("#1") ? 1 : 2;
                                            return { year, month, day, timeSuffix };
                                        };

                                        const dateA = extractDateComponents(a["Attachment1"]);
                                        const dateB = extractDateComponents(b["Attachment1"]);

                                        if (dateA.year !== dateB.year) return dateA.year - dateB.year;
                                        if (dateA.month !== dateB.month) return new Date(dateA.month + " 1, 1970").getMonth() - new Date(dateB.month + " 1, 1970").getMonth();
                                        if (dateA.day !== dateB.day) return dateA.day - dateB.day;
                                        return dateA.timeSuffix - dateB.timeSuffix;
                                    });

                                    // Create the Excel file for the venue
                                    const wb = XLSX.utils.book_new();
                                    const ws = XLSX.utils.json_to_sheet(excelData);
                                    XLSX.utils.book_append_sheet(wb, ws, venueName);  // Set sheet name as the venue name
                                    XLSX.writeFile(wb, `${venueName} Artist Confirmations - ${month} ${year}.xlsx`);
                                    successCount++;
                                } catch (err) {
                                    failCount++;
                                    console.error(`generateAndDownloadExcel failed writing file for venue "${venueName}":`, err);
                                    alert(`Confirmation file failed for venue "${venueName}": ${err.message}\n\nCheck the browser console (F12) for the full error.`);
                                }

                                // After the last (staggered) file has been attempted,
                                // give a real, visible confirmation either way — so a
                                // working run is never indistinguishable from a silent
                                // failure again.
                                if (index === venueNames.length - 1) {
                                    setTimeout(() => {
                                        alert(`Confirmation email data: ${successCount} of ${venueNames.length} venue file(s) generated successfully${failCount > 0 ? `, ${failCount} failed (see above)` : ''}.\n\nCheck your Downloads folder for the .xlsx file(s).`);
                                    }, 400);
                                }
                            }, index * 800);
                        });
                    } catch (err) {
                        console.error('generateAndDownloadExcel failed while processing/sorting/writing data:', err);
                        alert(`Confirmation data generation failed: ${err.message}\n\nCheck the browser console (F12) for the full error — this used to fail silently.`);
                    }
                }).catch(err => {
                    console.error('generateAndDownloadExcel failed fetching clients:', err);
                    alert(`Failed to fetch client data from Firebase: ${err.message}`);
                });
            }).catch(err => {
                console.error('generateAndDownloadExcel failed fetching events:', err);
                alert(`Failed to fetch event data from Firebase: ${err.message}`);
            });
        }).catch(err => {
            console.error('generateAndDownloadExcel failed fetching venues:', err);
            alert(`Failed to fetch venue data from Firebase: ${err.message}`);
        });
    }
    
    const XLSX = require('xlsx');

    async function generateAndDownloadExcelBookingAndCalendar(month, year, ccEmails, bccEmails) {
        const monthNumber = ("0" + (new Date(`${month} 1 ${year}`).getMonth() + 1)).slice(-2);
        const startDate = new Date(Date.UTC(year, parseInt(monthNumber) - 1, 1));
        const endDate = new Date(Date.UTC(year, parseInt(monthNumber), 0, 23, 59, 59, 999));
    
        const staticEmail = "musicmattersbookings@gmail.com";
    
        // Define dynamic attachment file names
        const attachment1ExchangeCalendar = `Exchange Calendar-${month} ${year} V1.jpg`;
        const attachment1BookingList = `Exchange Booking List-${month} ${year} V1.pdf`;
        const attachment2ExchangeCalendar = `Exchange Calendar-${month} ${year} V1.pdf`;
        const attachment3ExchangeCalendar = `Exchange Calendar-${month} ${year} V1.ods`;
    
        // Format date strings for Firebase queries
        const startDateString = `${startDate.getUTCFullYear()}-${("0" + (startDate.getUTCMonth() + 1)).slice(-2)}-${("0" + startDate.getUTCDate()).slice(-2)}`;
        const endDateString = `${endDate.getUTCFullYear()}-${("0" + (endDate.getUTCMonth() + 1)).slice(-2)}-${("0" + endDate.getUTCDate()).slice(-2)}`;
    
        // Firebase references
        const eventsRef = firebaseDb.child('database/events');
        const clientsRef = firebaseDb.child('database/clients');
        const venuesRef = firebaseDb.child('database/venues');
    
        // Fetch data from Firebase
        venuesRef.once('value').then(venuesSnapshot => {
            const venuesData = venuesSnapshot.val();
    
            Object.keys(venuesData).forEach(venueId => {
                const venue = venuesData[venueId];
                const venueName = (venue && venue.name === "Renaissance-Exchange") ? "Exchange" : (venue ? venue.name : "UnknownVenue");
    
                // Only process "Renaissance-Exchange" venue
                if (venueName !== "Exchange") {
                    return; // Skip this venue if it's not "Renaissance-Exchange"
                }
    
                // Use venue_month_year index (same approach as GeneratePDF.js) so that
                // events whose 'date' field is a number instead of a "YYYY-MM-DD" string
                // are not silently omitted by a string-range Firebase query.
                eventsRef.orderByChild('venue_month_year').equalTo(`${venueId}__${month}__${year}`).once('value').then(eventsSnapshot => {
                    const eventsData = eventsSnapshot.val();
                    if (!eventsData) return;

                    clientsRef.once('value').then(clientsSnapshot => {
                        const clientsData = clientsSnapshot.val();
                        let excelData = [];

                        // Add headers for the Excel file
                        excelData.push({
                            "EmailAddress": "EmailAddress",
                            "CC": "CC",
                            "BCC": "BCC",
                            "Attachment1": "Attachment1",
                            "Attachment2": "Attachment2",
                            "Attachment3": "Attachment3",
                            "SubjectLine": "SubjectLine",
                            "Body": "Body"
                        });
    
                        // Populate rows based on event data
                        Object.values(eventsData).forEach((event, index) => {
                            // No secondary date or venue filter needed — venue_month_year
                            // already scopes results to this venue, month, and year.
                            if (!event.clientID) return;
    
                            const client = clientsData[event.clientID];
                            if (!client) return;
    
                            const email = client.email;
                            let attachment1, attachment2, attachment3, subjectLine;
    
                            // Define file names and subject lines dynamically
                            if (index < 9) {
                                subjectLine = `Exchange Booking Calendar-${month} ${year} V1`;
                                attachment1 = attachment1ExchangeCalendar;
                                attachment2 = "";
                                attachment3 = "";
    
                                if (index === 2) {
                                    attachment2 = attachment2ExchangeCalendar;
                                    attachment3 = attachment3ExchangeCalendar;
                                }
    
                                if (index === 8 || index === 9) {
                                    subjectLine = `Exchange Booking List-${month} ${year} V1`;
                                    attachment1 = attachment1BookingList;
                                }
                            }
    
                            // Add event row to the Excel data
                            excelData.push({
                                "EmailAddress": staticEmail,
                                "CC": ccEmails[index] || "",
                                "BCC": bccEmails[index] || "",
                                "Attachment1": attachment1,
                                "Attachment2": attachment2,
                                "Attachment3": attachment3,
                                "SubjectLine": subjectLine,
                                "Body": ""
                            });
                        });
    
                        // Create the Excel file and write data to it
                        const wb = XLSX.utils.book_new();
                        const ws = XLSX.utils.json_to_sheet(excelData, { skipHeader: true });
                        XLSX.utils.book_append_sheet(wb, ws, "Detailed Data");
    
                        // Define the file name and save it
                        const fileName = `${venueName} ${month} ${year} Detailed Artist Booking List and Calendar.xlsx`;
                        XLSX.writeFile(wb, fileName);
                    });
                });
            });
        });
    }
    
    // formats download and preview buttons
    function VenueCard({ index, venue, venueID, month, year, togglePreview }) {
        const bookings = BookingListData(month, year, venueID);
        const confirmations = ConfirmationData(month, year, venueID);
        const invoices = InvoiceData(month, year, venueID);
        const subjectLines = SubjectLineData(month, year, venueID);

        return (
            <div key={index} className="card-body pb-0">
                {/* Venue Name */}
                <div className="card-header main-search dash-search">
                    <h3>{venue.name}</h3>
                </div>

                {/* Booking List overhaul */}
                <div className="card-body pb-0">
                    <div className="card">
                        <div className="card-header">Booking List</div>
                        <div className="card-body position-relative">
                            <Stack
                                direction="row"
                                justifyContent="flex-start"
                                alignItems="center"
                                spacing={2}
                            >
                                <Button
                                    variant="contained"
                                    id={venue.name + "_bookinglist-preview"}
                                    onClick={togglePreview}
                                >
                                    Preview
                                </Button>

                                <BookingListDownload
                                    bookings={bookings}
                                    venue={venue}
                                    month={month}
                                    year={year}
                                />

                                <div style={{ flex: '1 0 0' }} />
                                <span className="text-secondary">
                                    Last Sent:&nbsp;&nbsp;{
                                        (new Date(venue.bookingListLastSent).toLocaleDateString() !== "Invalid Date")
                                            ? new Date(venue.bookingListLastSent).toLocaleDateString()
                                            : "Unknown"
                                    }
                                </span>
                            </Stack>

                            <div
                                id={venue.name + "_bookinglist"}
                                style={{
                                    marginTop: "10px",
                                    overflow: "auto",
                                    paddingBottom: "-16px",
                                    display: "none"
                                }}
                            >
                                <BookingListViewer
                                    bookings={bookings}
                                    venue={venue}
                                    month={month}
                                    year={year}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                { /* Artist Confirmations overhaul */ }
                    <div className="card-body pb-0">
                        <div className="card">
                            <div className="card-header">Artist Confirmations</div>
                            <div className="card-body position-relative">
                                <Stack
                                    direction="row"
                                    justifyContent="flex-start"
                                    alignItems="center"
                                    spacing={2}
                                >
                                    <Button
                                        variant="contained"
                                        id={venue.name + "_artistconfirmation-preview"}
                                        onClick={togglePreview}
                                    >
                                        Preview
                                    </Button>

                                    <ConfirmationsDownload
                                        confirmations={confirmations}
                                        venue={venue}
                                        month={month}
                                        year={year}
                                    />

                                    <div style={{ flex: '1 0 0' }} />
                                    <span className="text-secondary">
                                        Last Sent:&nbsp;&nbsp;{
                                            (new Date(venue.allConfirmationsLastSent).toLocaleDateString() !== "Invalid Date")
                                                ? new Date(venue.allConfirmationsLastSent).toLocaleDateString()
                                                : "Unknown"
                                        }
                                    </span>
                                </Stack>

                                <div
                                    id={venue.name + "_artistconfirmation"}
                                    style={{
                                        marginTop: "10px",
                                        overflow: "auto",
                                        paddingBottom: "-16px",
                                        display: "none"
                                    }}
                                >
                                    <ConfirmationsViewer
                                        confirmations={confirmations}
                                        venue={venue}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                { /* Artist Invoices overhaul */ }
                <div className="card-body pb-0">
                    <div className="card">
                        <div className="card-header">Artist Invoices</div>
                        <div className="card-body position-relative">
                            <Stack
                                direction="row"
                                justifyContent="flex-start"
                                alignItems="center"
                                spacing={2}
                            >

                                <Button
                                    variant="contained"
                                    id={venue.name + "_artistinvoice-preview"}
                                    onClick={togglePreview}
                                >
                                    Preview
                                </Button>

                                <InvoicesDownload
                                    invoices={invoices}
                                    venue={venue}
                                    month={month}
                                    year={year}
                                />

                                <div style={{ flex: '1 0 0' }} />
                                <span className="text-secondary">
                                    Last Sent:&nbsp;&nbsp;{
                                        (new Date(venue.allInvoicesLastSent).toLocaleDateString() !== "Invalid Date")
                                            ? new Date(venue.allInvoicesLastSent).toLocaleDateString()
                                            : "Unknown"
                                    }
                                </span>
                            </Stack>

                            <div
                                id={venue.name + "_artistinvoice"}
                                style={{
                                    marginTop: "10px",
                                    overflow: "auto",
                                    paddingBottom: "-16px",
                                    display: "none"
                                }}
                            >
                                <InvoicesViewer
                                    invoices={invoices}
                                    venue={venue}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* PDF Names & Email Subject Lines List overhaul */}
                <div className="card-body pb-3">
                    <div className="card">
                        <div className="card-header">PDF Names & Email Subject Lines List</div>
                        <div className="card-body position-relative">
                            <Stack
                                direction="row"
                                justifyContent="flex-start"
                                alignItems="center"
                                spacing={2}
                            >
                                <Button
                                    variant="contained"
                                    id={venue.name + "_subjectlist-preview"}
                                    onClick={togglePreview}
                                >
                                    Preview
                                </Button>

                                <SubjectLineDownload
                                    bookings={subjectLines}
                                    venue={venue}
                                    month={month}
                                    year={year}
                                />

                                <div style={{ flex: '1 0 0' }} />
                                <span className="text-secondary">
                                    Last Sent:&nbsp;&nbsp;{
                                        (new Date(venue.allInvoicesLastSent).toLocaleDateString() !== "Invalid Date")
                                            ? new Date(venue.allInvoicesLastSent).toLocaleDateString()
                                            : "Unknown"
                                    }
                                </span>
                            </Stack>

                            <div
                                id={venue.name + "_subjectlist"}
                                style={{
                                    marginTop: "10px",
                                    overflow: "auto",
                                    paddingBottom: "-16px",
                                    display: "none"
                                }}
                            >
                                <SubjectLineViewer
                                    bookings={subjectLines}
                                    venue={venue}
                                    month={month}
                                    year={year}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
    
    return (
        <div className='content' >
            {/* Card containing all other cards */}
            
            <div className="card" >
                {/* Header displaying which month and year the documents are for */}
                <div className="card-header main-search dash-search"> 
                    <Stack
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="center"
                        spacing={2}
                    >
                          
                        <h3>{month} {year} - Documents</h3>
                        <Button variant="contained" size="small" onClick={() => generateAndDownloadExcelBookingAndCalendar(month, year)}>Download Detailed Monthly Artist Booking and Calendar Email Data </Button>
                        <Button variant="contained" size="small" onClick={() => generateAndDownloadExcel(month, year)}>Download Monthly Artist Confirmation Email Data</Button>

                        <div style={{flex: '1 0 0'}} />
                        {/* Month Selector */}
                        <FormControl variant="filled" sx={{ m: 1, minWidth: 135 }}>
                            <InputLabel id="demo-simple-select-label">Month</InputLabel>
                            <Select
                                labelId="documents-month-select-label"
                                id="documents-month-select"
                                value={month}
                                label="Month"
                                onChange={handleMonthChange}
                            >
                                <MenuItem value={"January"}>January</MenuItem>
                                <MenuItem value={"February"}>February</MenuItem>
                                <MenuItem value={"March"}>March</MenuItem>
                                <MenuItem value={"April"}>April</MenuItem>
                                <MenuItem value={"May"}>May</MenuItem>
                                <MenuItem value={"June"}>June</MenuItem>
                                <MenuItem value={"July"}>July</MenuItem>
                                <MenuItem value={"August"}>August</MenuItem>
                                <MenuItem value={"September"}>September</MenuItem>
                                <MenuItem value={"October"}>October</MenuItem>
                                <MenuItem value={"November"}>November</MenuItem>
                                <MenuItem value={"December"}>December</MenuItem>
                            </Select>
                        </FormControl>
                        {/* Year Selector */}
                        {(yearsList !== undefined && yearsList.length !== 0) && <FormControl variant="filled" sx={{ m: 1, minWidth: 90 }}>
                            <InputLabel id="demo-simple-select-label">Year</InputLabel>
                            <Select
                                labelId="documents-year-select-label"
                                id="documents-year-select"
                                value={year}
                                label="Year"
                                onChange={handleYearChange}
                            >
                                {yearsList.map((option) => (
                                    <MenuItem key={option} value={option}>{option}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>}
                    </Stack>
                </div>

                {/* Card for each venue containing documents */}
                {Object.keys(venues).map((key, i) => (
                    <VenueCard
                        key={i}
                        index={i}
                        venue={venues[key]}
                        venueID={key}
                        month={month}
                        year={year}
                        togglePreview={togglePreview}
                    />
                ))}
            </div>
        </div>
    )
}

export default Documents;